// BasicInfoForm —— 基本信息段，里面再分 tab。
//
// Tab 划分（按用户反馈）：
//   - 服务信息（service-level，updateBasic）：name / desc / category / logoUrl /
//     host / isPublic / accessMode / enableDetailedLogs
//   - 上游接入（version-level，updateVersion）：baseUrl / authType / authConfig /
//     privateHeaders
//
// 保存按 react-hook-form 的 dirtyFields 智能分发：service 字段 dirty 走
// updateBasic，version 字段 dirty 走 updateVersion，两者都 dirty 就串行调。
// 这避免了"切个 tab 然后保存按钮去哪了"的体验。

import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import { useForm, FormProvider, Controller, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { XPrim, XIcons } from '../../primitives.jsx';
import { XShell } from '../../shell.jsx';
import { XApi } from '../../services.js';
import { useHostValidation } from '../../use-host-validation.js';
import { Dialog, DialogHeader, DialogBody } from '../../ui/index.js';
import { useT } from '../../i18n.jsx';
import { MarkdownEditor } from '../../components/MarkdownEditor.jsx';
import { ABOUT_DOC_DEMO, ABOUT_DOC_STARTER, buildSandboxAboutDoc } from '../../data/about-doc-demo.js';
import { PublishDialog } from './version-shared.jsx';
import { buildSandboxBindingPayload } from './sandbox-provider-section.jsx';
import {
  INITIAL_FORM,
  apiFormBaseSchema,
  apiFormSchema,
  CATEGORIES,
  transformApiToForm,
  buildBasicPayload,
  buildVersionPayload,
  OverviewSection,
  OriginAuthSection,
  DirectModeNotice,
  StatusBanner,
  ConfirmDialog,
} from '../register.jsx';

const SERVICE_LEVEL_FIELDS = [
  'name',
  'description',
  'website',
  'aboutMarkdown',
  'category',
  'logoUrl',
  'host',
  'isPublic',
  'accessMode',
  'enableDetailedLogs',
  'rateLimitConfig',
];
const VERSION_LEVEL_FIELDS = ['baseUrl', 'baseUrls', 'authType', 'authConfig', 'privateHeaders'];

// 基础信息表单只编辑「服务元数据 + 上游接入」,不编辑端点。共享的 apiFormSchema 含端点
// 校验(AI 端点需 servedModels 等)——若不放宽,从模板创建的草稿(端点尚未配模型)会让
// 基础信息也「有 1 个字段需要修正」却无处可改。这里把端点校验放宽,端点的校验/保存留给
// 「管理 API」段。
// Sandbox 配置已随端点保存（billingType=SANDBOX），基础信息不再校验它。
const basicInfoSchema = apiFormBaseSchema.extend({ endpoints: z.array(z.any()) });

const TABS = [
  { key: 'overview', label: '服务信息', hint: '展示在市场 / MCP 工具列表里的元数据' },
  { key: 'about',    label: 'About 文档', hint: '详情页 About 长文档（Markdown）' },
  { key: 'origin',   label: '上游接入', hint: '版本契约：baseUrl / 认证 / 私有请求头' },
];

// 错误字段 → { 中文标签, 所在 tab }，用于保存条里把错误标出来 + 一键跳过去。
const FIELD_META = {
  name:           { label: '展示名称',     tab: 'overview' },
  description:    { label: '描述',         tab: 'overview' },
  website:        { label: '官网',         tab: 'overview' },
  aboutMarkdown:  { label: 'About 文档',   tab: 'about' },
  logoUrl:        { label: 'Logo URL',     tab: 'overview' },
  host:           { label: '域名前缀',     tab: 'overview' },
  category:       { label: '分类',         tab: 'overview' },
  rateLimitConfig:{ label: '用户频率限制', tab: 'overview' },
  baseUrl:        { label: '默认 Base URL', tab: 'origin' },
  baseUrls:       { label: '命名 Base URL', tab: 'origin' },
  authType:       { label: '鉴权方式',     tab: 'origin' },
  authConfig:     { label: 'Secret',       tab: 'origin' },
  privateHeaders: { label: '私有请求头',   tab: 'origin' },
};

/** About markdown editor tab — service-level, saved via updateBasic. */
const AboutDocSection = () => {
  const { control, formState: { errors }, setValue, watch } = useFormContext();
  const { t } = useT();
  // Template-replace confirmation as a design-system Dialog (was native confirm()).
  const [tplConfirm, setTplConfirm] = useState(null); // 'starter' | 'demo' | null
  const about = watch('aboutMarkdown') || '';
  const applyTemplate = (kind) =>
    setValue('aboutMarkdown', kind === 'demo' ? ABOUT_DOC_DEMO : ABOUT_DOC_STARTER, { shouldDirty: true });
  const requestTemplate = (kind) => {
    if (about.trim()) setTplConfirm(kind);
    else applyTemplate(kind);
  };
  return (
    <div className="col gap-3" style={{ padding: '0 0 8px' }}>
      <div className="row between" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ color: 'var(--fg-3)', fontSize: 13, lineHeight: 1.5, maxWidth: 560 }}>
          这篇 Markdown 会出现在市场详情页的 <strong>About</strong> tab。
          短描述（服务信息）仍用于市场卡片。建议结构：Overview → Highlights → FAQ → Capabilities → Credits → Use cases → Response / Terms。
        </div>
        <div className="row gap-2" style={{ flexShrink: 0 }}>
          <button
            type="button"
            className="btn btn-sm"
            title="Insert a short skeleton (keeps empty sections to fill)"
            onClick={() => requestTemplate('starter')}
          >
            Insert starter
          </button>
          <button
            type="button"
            className="btn btn-sm"
            title="Full Rapid-style demo document"
            onClick={() => requestTemplate('demo')}
          >
            Load full demo
          </button>
        </div>
      </div>
      {errors.aboutMarkdown && (
        <div style={{ color: 'var(--danger, #dc2626)', fontSize: 12 }}>
          {errors.aboutMarkdown.message || 'About 文档无效'}
        </div>
      )}
      <Controller
        name="aboutMarkdown"
        control={control}
        render={({ field }) => (
          <MarkdownEditor
            value={field.value}
            onChange={field.onChange}
            height={480}
            placeholder={ABOUT_DOC_STARTER}
            maxLength={100_000}
          />
        )}
      />
      <ConfirmDialog
        open={!!tplConfirm}
        onClose={() => setTplConfirm(null)}
        onConfirm={() => applyTemplate(tplConfirm)}
        title={t('register.overview.about.replaceTitle')}
        body={t('register.overview.about.replaceBody')}
        confirmLabel={t('register.overview.about.replaceOk')}
        danger
      />
    </div>
  );
};

export const BasicInfoForm = ({ api, selectedMajor, inReview, onApiRefreshed, onDirtyChange }) => {
  const { toast } = XPrim.useToast();
  const { t } = useT();
  const [logoUploading, setLogoUploading] = useState(false);
  const { navigate } = XShell.useRoute();
  const [submitting, setSubmitting] = useState(null); // null | 'save' | 'review' | 'publish'
  const [activeTab, setActiveTab] = useState('overview');
  const [submitIssues, setSubmitIssues] = useState(null); // 提审前全量检查的待修正项
  const [publishState, setPublishState] = useState(null); // { major, workingId }

  const methods = useForm({
    resolver: zodResolver(basicInfoSchema),
    defaultValues: api ? transformApiToForm(api) : INITIAL_FORM(),
    mode: 'onBlur',
  });
  const { handleSubmit, reset, watch, formState: { isDirty, errors, dirtyFields } } = methods;

  useEffect(() => {
    if (api) reset(transformApiToForm(api));
  }, [
    api?.id,
    api?.updatedAt,
    api?.currentVersion?.id,
    api?.sandboxBinding?.updatedAt,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // 把未保存状态 + 保存能力上报给工作区外壳:切 tab 时弹「保存并离开 / 放弃并离开 / 取消」
  // (见 workspace-api)。save 在调用时读最新表单状态,不怕闭包过期。
  useEffect(() => {
    onDirtyChange?.({ isDirty: isDirty || logoUploading, hasErrors: logoUploading || Object.keys(errors).length > 0, save: () => saveAndReport() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, errors, logoUploading, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(null), [onDirtyChange]); // 卸载时清除

  const accessMode = watch('accessMode');
  const isDirect = accessMode === 'DIRECT';
  const visibleTabs = TABS;

  // Host-prefix realtime validation (advisory — backend re-checks on save).
  // Edit mode: excludeId=self for uniqueness; skipValue=original host so an unchanged
  // grandfathered (reserved/taken) host stays idle and never blocks unrelated saves.
  const formHost = watch('host');
  const originalHostPrefix = (api?.host || '')
    .replace(/\.p\.xapi\.to$/i, '').replace(/^https?:\/\//i, '').split('.')[0] || '';
  const hostValidation = useHostValidation(formHost, {
    excludeId: api?.id,
    skipValue: originalHostPrefix,
    enabled: !isDirect,
  });

  // 把当前表单里 dirty 的字段落库:service 字段走 updateBasic,version 字段走
  // updateVersion(两者都 dirty 就串行调)。onSave 与「提交审核」都先经过它。
  // 编辑目标版本：已上线 API 的版本级编辑走工作副本(copy-on-write，沙箱)，线上不动；
  // 草稿/沙箱就地改当前版本。与「管理 API」段保存一致。服务级 metadata(updateBasic)不版本化。
  const resolveEditTarget = async () => {
    try {
      const ov = await XApi.apiSvc.versionOverview(api.id);
      const major =
        ov?.majors?.find((m) => m.major === selectedMajor) ||
        ov?.majors?.find((m) => m.isDefault) ||
        ov?.majors?.[0];
      if (major?.working) return major.working.id;
      if (major?.live) return (await XApi.apiSvc.startEdit(api.id, major.major)).id;
    } catch { /* 回退当前版本 */ }
    return api?.activeVersionId || api?.activeVersion?.id;
  };

  const dirtyKinds = (df) => ({
    needsBasic: SERVICE_LEVEL_FIELDS.some((f) => df[f]),
    needsVersion: VERSION_LEVEL_FIELDS.some((f) => df[f]),
  });

  const persistDirty = async (form) => {
    if (logoUploading) throw new Error(t('logo.pending'));
    const df = methods.formState.dirtyFields;
    // Soft-block only when the host was actually changed to an invalid value — an
    // unchanged grandfathered host stays idle (hook skips it) and must not block saves.
    if (df.host && hostValidation.status === 'invalid') {
      setActiveTab('overview');
      throw new Error(hostValidation.message);
    }
    const { needsBasic, needsVersion } = dirtyKinds(df);
    if (needsBasic) {
      await XApi.apiSvc.updateBasic(api.id, buildBasicPayload(form));
    }
    if (needsVersion) {
      const target = await resolveEditTarget();
      if (!target) throw new Error('找不到当前版本，无法保存上游接入字段');
      // 只更新上游接入元数据(baseUrl/auth/baseUrls/privateHeaders),不带 endpoints ——
      // 基础信息段不编辑端点;省略 endpoints 让后端跳过端点同步/校验(端点的保存在「管理 API」段)。
      const { endpoints: _omitEndpoints, ...versionMeta } = buildVersionPayload(form);
      await XApi.apiSvc.updateVersion(api.id, target, versionMeta);
    }
    return { needsBasic, needsVersion };
  };

  const refreshApi = async () => {
    const fresh = await XApi.apiSvc.get(api.id);
    if (fresh) {
      reset(transformApiToForm(fresh));
      onApiRefreshed?.(fresh);
    }
  };

  // 给「保存并离开」用:保存并返回是否成功(true=已落库,可安全离开)。读最新表单状态。
  const saveAndReport = () =>
    new Promise((resolve) => {
      if (logoUploading) {
        toast({ title: t('logo.pending'), type: 'info' });
        resolve(false);
        return;
      }
      methods.handleSubmit(
        async (form) => {
          const df = methods.formState.dirtyFields;
          const { needsBasic, needsVersion } = dirtyKinds(df);
          if (!needsBasic && !needsVersion) { resolve(true); return; }
          setSubmitting('save');
          try {
            await persistDirty(form);
            toast({ title: '已保存', type: 'success' });
            await refreshApi();
            resolve(true);
          } catch (e) {
            toast({ title: e?.message || '保存失败', type: 'error' });
            resolve(false);
          } finally {
            setSubmitting(null);
          }
        },
        () => { toast({ title: '请先修正高亮字段', type: 'error' }); resolve(false); },
      )();
    });

  const onSave = handleSubmit(
    async (form) => {
      if (!api?.id) return;
      const { needsBasic, needsVersion } = dirtyKinds(dirtyFields);
      if (!needsBasic && !needsVersion) {
        toast({ title: '没有改动', type: 'info' });
        return;
      }
      setSubmitting('save');
      try {
        await persistDirty(form);
        toast({
          title: needsBasic && needsVersion ? '已保存（含版本契约更新）' : '已保存',
          type: 'success',
        });
        await refreshApi();
      } catch (e) {
        toast({ title: e?.message || '保存失败', type: 'error' });
      } finally {
        setSubmitting(null);
      }
    },
    () => toast({ title: '请先修正高亮字段', type: 'error' }),
  );

  const onPublish = handleSubmit(
    async (form) => {
      if (!api?.id || selectedMajor == null || logoUploading) return;
      if (api.status === 'SUSPENDED') {
        toast({
          title: '服务已由管理员暂停；请联系管理员恢复，无需重新提交审核',
          type: 'error',
        });
        return;
      }
      setSubmitting('publish');
      try {
        const df = methods.formState.dirtyFields;
        const { needsBasic, needsVersion } = dirtyKinds(df);
        if (needsBasic || needsVersion) await persistDirty(form);
        const target = await resolveEditTarget();
        if (!target) throw new Error('找不到要发布的版本');
        reset(form); // 已落库，清 dirty
        setPublishState({ major: selectedMajor, workingId: target });
      } catch (e) {
        toast({ title: e?.message || '发布准备失败', type: 'error' });
      } finally {
        setSubmitting(null);
      }
    },
    () => toast({ title: '请先修正高亮字段', type: 'error' }),
  );

  // 提审前「全量检查」—— 这是唯一一处同时管「服务信息 + 所有端点」的总闸：
  // 用严格的 apiFormSchema(含端点 AI 模型完整性)校验整份表单,把所有待修正项一次性列出。
  // 注:基础信息表单的端点来自加载时的 api 数据(端点编辑在「管理 API」段、独立表单),
  // 这里能抓到 DB 里已保存端点的不完整;后端 submitForReview 仍是最终兜底。
  const collectSubmitIssues = (values) => {
    const res = apiFormSchema.safeParse(values);
    if (res.success) return [];
    const seen = new Set();
    const out = [];
    for (const iss of res.error.issues) {
      const top = iss.path[0];
      if (top === 'endpoints') {
        const idx = iss.path[1];
        if (seen.has('ep' + idx)) continue;
        seen.add('ep' + idx);
        const ep = values.endpoints?.[idx];
        out.push({ location: 'apis', endpointId: ep?.id, label: `端点「${ep?.name || ep?.path || '#' + (idx + 1)}」需要补全（如服务模型）` });
      } else {
        if (seen.has('f' + top)) continue;
        seen.add('f' + top);
        const meta = FIELD_META[top];
        out.push({ location: 'basic', tab: meta?.tab, field: top, label: meta?.label || top });
      }
    }
    return out;
  };

  const doSubmitReview = async () => {
    setSubmitting('review');
    try {
      await persistDirty(methods.getValues());
      await XApi.apiSvc.submitForReview(api.id, '');
      toast({ title: '已提交审核', type: 'success' });
      await refreshApi();
    } catch (e) {
      toast({ title: e?.message || '提交审核失败', type: 'error' });
    } finally {
      setSubmitting(null);
    }
  };

  // 提交审核:先跑全量检查;有问题就弹「提审前检查」清单(可逐项跳转),全绿才真正提交。
  const onSubmitReview = () => {
    if (!api?.id || logoUploading) return;
    const issues = collectSubmitIssues(methods.getValues());
    if (issues.length > 0) {
      setSubmitIssues(issues);
      return;
    }
    if (!confirm('提交审核后，管理员会看到当前工作副本。确定提交？')) return;
    doSubmitReview();
  };

  // 跳到某条待修正项:基础信息字段 → 切 tab + 定位;端点 → 去「管理 API」段并选中该具体端点
  // (用 sessionStorage 传递目标端点 id,EndpointsManager 挂载时读取并选中,见其 effect)。
  const jumpToIssue = (it) => {
    setSubmitIssues(null);
    if (it.location === 'apis') {
      if (it.endpointId) sessionStorage.setItem('xapi.jumpEndpoint', it.endpointId);
      navigate('workspace-api', { apiId: api.id, section: 'apis' });
    } else if (it.field) {
      jumpToError(it.field);
    }
  };

  if (!api) {
    return <div style={{ padding: 32, color: 'var(--fg-2)' }}>API not loaded</div>;
  }

  const errorCount = Object.keys(errors).length;
  const savedDisabled = submitting || logoUploading || !isDirty || errorCount > 0;
  // 出错的字段(顶层 key),用于保存条标记 + 跳转。
  const errorFields = Object.keys(errors);
  // 跳到出错字段:先切到它所在 tab,再滚动 + focus 对应输入。
  const jumpToError = (field) => {
    const meta = FIELD_META[field];
    if (meta?.tab) setActiveTab(meta.tab);
    requestAnimationFrame(() => {
      const el =
        document.querySelector(`[name="${field}"]`) ||
        document.querySelector(`[name^="${field}."]`) ||
        document.querySelector(`[name^="${field}["]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof el.focus === 'function') setTimeout(() => el.focus(), 250);
      }
    });
  };

  const serviceSuspended = api?.status === 'SUSPENDED';
  const publishDisabled =
    serviceSuspended ||
    inReview ||
    submitting != null ||
    logoUploading ||
    errorCount > 0 ||
    selectedMajor == null;

  return (
    <FormProvider {...methods}>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
        {/* sticky 顶部 */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            background: 'var(--bg-0)',
            borderBottom: '1px solid var(--bd-1)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 32px 0',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: 'var(--fg-0)', letterSpacing: '-0.01em' }}>
                基本信息
              </h2>
              <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4 }}>
                {TABS.find((t) => t.key === activeTab)?.hint}
              </div>
            </div>
            {/* 右上角「保存 / 发布」。保存 → 所选大版本工作副本（沙箱，线上不动）；
                发布 → 弹「改了什么 · 发布到 v{N}」确认上线。 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {selectedMajor != null && (
                inReview ? (
                  <span style={{ fontSize: 11, color: '#2563eb', whiteSpace: 'nowrap', fontWeight: 600 }}>
                    <span className="ws-spin" style={{ width: 9, height: 9, borderRadius: '50%', border: '2px solid #2563eb', borderTopColor: 'transparent', display: 'inline-block', marginRight: 5 }} />
                    v{selectedMajor} 审核中
                  </span>
                ) : (
                  <span style={{ fontSize: 11, color: 'var(--fg-3)', whiteSpace: 'nowrap' }}>
                    正在编辑 <b style={{ color: 'var(--fg-1)' }}>v{selectedMajor}</b>
                  </span>
                )
              )}
              {!inReview && isDirty && (
                <span style={{ fontSize: 11, color: 'var(--warn, #e08e0b)', whiteSpace: 'nowrap' }}>● 未保存</span>
              )}
              {!inReview && errorCount > 0 && (
                <span style={{ fontSize: 12, color: '#e06b5e', whiteSpace: 'nowrap' }}>{errorCount} 项需修正</span>
              )}
              <button
                type="button"
                onClick={onSave}
                disabled={savedDisabled || inReview}
                title={inReview ? '审核中，暂不可编辑' : errorCount > 0 ? `有 ${errorCount} 个字段需修正` : `保存到 v${selectedMajor ?? ''}（进沙箱工作副本，线上不动）`}
                style={{
                  padding: '8px 16px', borderRadius: 6, border: '1px solid var(--bd-1)',
                  background: (savedDisabled || inReview) ? 'var(--bg-1)' : 'var(--bg-0)',
                  color: (savedDisabled || inReview) ? 'var(--fg-3)' : 'var(--fg-0)',
                  fontSize: 13, fontWeight: 600,
                  cursor: (savedDisabled || inReview) ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting === 'save' ? '保存中…' : '保存'}
              </button>
              <button
                type="button"
                onClick={onPublish}
                disabled={publishDisabled}
                title={serviceSuspended ? '服务已由管理员暂停；恢复由管理员操作，无需重新提交审核' : inReview ? '已提交，审核中…' : errorCount > 0 ? `有 ${errorCount} 个字段需修正` : `发布到 v${selectedMajor ?? ''}（确认前会显示改动）`}
                style={{
                  padding: '8px 16px', borderRadius: 6, border: 'none',
                  background: publishDisabled ? 'var(--bg-1)' : 'var(--fg-0)',
                  color: publishDisabled ? 'var(--fg-3)' : 'var(--bg-0)',
                  fontSize: 13, fontWeight: 600,
                  cursor: publishDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                {inReview ? '审核中…' : submitting === 'publish' ? '准备中…' : '发布'}
              </button>
            </div>
          </div>

          {/* Tabs row */}
          <div
            style={{
              display: 'flex',
              gap: 4,
              padding: '14px 32px 0',
              marginTop: 10,
            }}
          >
            {visibleTabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                style={{
                  padding: '8px 14px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: activeTab === t.key
                    ? '2px solid var(--fg-0)'
                    : '2px solid transparent',
                  color: activeTab === t.key ? 'var(--fg-0)' : 'var(--fg-3)',
                  fontSize: 13,
                  fontWeight: activeTab === t.key ? 500 : 400,
                  cursor: 'pointer',
                  marginBottom: -1,
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ padding: '24px 32px 80px', maxWidth: 920, width: '100%' }}>
          {/* 只在需要提醒的状态(草稿/沙箱/待审/被拒/暂停)显示状态条。ACTIVE/APPROVED
              常态不再在这里重复一行绿字 —— 状态已在侧栏,版本策略移到「保存」按钮的
              tooltip,内容直接从表单开始,不在顶部堆advisory小字。 */}
          {api?.status &&
            api.status !== 'ACTIVE' &&
            api.status !== 'APPROVED' && <StatusBanner status={api.status} />}

          {/* key={activeTab} replays the quick fade on tab switch. */}
          <div key={activeTab} className="apx-fade">
            {activeTab === 'overview' && <OverviewSection hostValidation={hostValidation} onLogoUploadingChange={setLogoUploading} disabled={inReview || submitting != null} />}
            {activeTab === 'about' && <AboutDocSection />}
            {activeTab === 'origin' && (isDirect ? <DirectModeNotice /> : (
              <OriginAuthSection
                onSandboxDetected={async (endpoint, _cfg, probe) => {
                  // 本段不保存端点（下方 persistDirty 显式剔除 endpoints），所以自动
                  // 配置必须自己落库：先存上游地址，再写入 Sandbox 端点，然后把人
                  // 送到「管理 API」填价格。
                  console.log('[sandbox-auto] fired', { apiId: api?.id, endpoint });
                  if (!api?.id) { console.warn('[sandbox-auto] no api id'); return; }
                  setSubmitting('save');
                  try {
                    // 认出是 Sandbox Adapter，分类就该跟着走 —— 服务商没理由再去
                    // 手动改一次，漏改的话服务会挂在错误的类目下被人搜不到。
                    // 已经明确选过 Sandbox 之外的分类则不覆盖（尊重显式选择）。
                    const currentCategory = methods.getValues('category');
                    if (!currentCategory || currentCategory === CATEGORIES[0]) {
                      methods.setValue('category', 'Sandbox', { shouldDirty: true });
                    }
                    // About 文档同理：Sandbox 的接口面由协议固定，"怎么调用"这部分
                    // 完全能按探测到的能力写出来，手写反而会和真实接口面漂移
                    // （能力是按 manifest 收窄的）。只在空文档时写入，不覆盖已有内容。
                    if (!String(methods.getValues('aboutMarkdown') || '').trim()) {
                      methods.setValue('aboutMarkdown', buildSandboxAboutDoc({
                        name: methods.getValues('name'),
                        provider: probe?.provider,
                        capabilities: probe?.capabilities,
                        skus: probe?.skus,
                      }), { shouldDirty: true });
                    }
                    await persistDirty(methods.getValues());
                    const target = await resolveEditTarget();
                    if (!target) throw new Error('找不到当前版本');
                    const current = await XApi.apiSvc.get(api.id);
                    const existing = (current?.activeVersion?.endpoints || [])
                      .filter((ep) => ep.billingType !== 'SANDBOX')
                      .map((ep) => ({ id: ep.id, name: ep.name, method: ep.method, path: ep.path, billingType: ep.billingType }));
                    await XApi.apiSvc.updateVersion(api.id, target, {
                      endpoints: [
                        ...existing,
                        {
                          name: endpoint.name,
                          method: endpoint.method,
                          path: endpoint.path,
                          billingType: 'SANDBOX',
                          sandboxConfig: buildSandboxBindingPayload(endpoint.sandboxConfig),
                        },
                      ],
                    });
                    toast({ title: '已按 Adapter 自动配置，请填写价格', type: 'success' });
                    await refreshApi();
                    navigate(`workspace-api`, { apiId: api.id, section: 'apis' });
                  } catch (e) {
                    console.error('[sandbox-auto] failed', e);
                    toast({ title: e?.message || '自动配置失败', type: 'error' });
                  } finally {
                    setSubmitting(null);
                  }
                }}
              />
            ))}
          </div>
        </div>

        {/* 底部保存条已移除——「保存」在右上角（与「管理 API」段一致）。 */}

        {/* 提审前全量检查清单：服务信息 + 端点的所有待修正项,逐项可跳转。 */}
        {submitIssues && (
          <Dialog open onClose={() => setSubmitIssues(null)} maxWidth={460}>
            <DialogHeader title={`提交审核前还有 ${submitIssues.length} 项要完成`} onClose={() => setSubmitIssues(null)} />
            <DialogBody>
              <div style={{ fontSize: 13, color: 'var(--fg-2)', marginBottom: 12, lineHeight: 1.6 }}>
                上架前需要把<strong>服务信息</strong>和<strong>所有端点</strong>都配置完整。点击某项可直接跳过去修正：
              </div>
              <div className="col gap-2">
                {submitIssues.map((it, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => jumpToIssue(it)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                      padding: '10px 12px', borderRadius: 8, cursor: 'pointer', width: '100%', textAlign: 'left',
                      border: '1px solid rgba(192,57,43,0.4)', background: 'rgba(192,57,43,0.08)', color: 'var(--fg-1)',
                    }}
                  >
                    <span style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      {it.location === 'apis'
                        ? <XIcons.IconCode size={13} style={{ color: '#e06b5e' }} />
                        : <XIcons.IconInfo size={13} style={{ color: '#e06b5e' }} />}
                      {it.label}
                    </span>
                    <span style={{ fontSize: 12, color: '#e06b5e', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      {it.location === 'apis' ? '去管理 API' : '去修正'}
                      <XIcons.IconArrowRight size={11} />
                    </span>
                  </button>
                ))}
              </div>
            </DialogBody>
          </Dialog>
        )}

        <PublishDialog
          open={!!publishState}
          apiId={api?.id}
          major={publishState?.major}
          workingId={publishState?.workingId}
          onClose={() => setPublishState(null)}
          onPublished={() => { setPublishState(null); onApiRefreshed?.(); }}
        />
      </div>
    </FormProvider>
  );
};
